import React, { useState, useRef, useEffect } from 'react';
import { Bell, Check, MessageSquare, Briefcase, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, useMarkAsRead, useMarkAllAsRead, useDeleteNotification } from '../hooks/use-notifications';
import { timeAgo } from '../../../utils/timeAgo';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();

  const { data: notifications = [] } = useNotifications();
  const { mutate: markAsRead } = useMarkAsRead();
  const { mutate: markAllAsRead } = useMarkAllAsRead();
  const { mutate: deleteNotification } = useDeleteNotification();

  const unreadCount = notifications.filter(n => !n.isRead).length;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    setIsOpen(false);
    
    if (notification.type === 'NEW_APPLICATION') {
      navigate(`/opportunities/${notification.entityId}/applicants`);
    } else if (notification.type === 'APPLICATION_STATUS_UPDATED') {
      navigate('/my-applications');
    } else if (
      notification.type.includes('ACCESS_') || 
      notification.type.includes('CLINICAL')
    ) {
      // For access requests and clinical inputs, the entityId is the caseId
      navigate(`/cases/${notification.entityId}`);
    } else {
      navigate(`/panels/${notification.entityId}`);
    }
  };

  const formatActorName = (notification) => {
    const actor = notification.actor || {};
    const rawName = notification.actorName || actor.name || 'User';
    const role = actor.role || notification.actorRole;
    if (role === 'student') return rawName;
    return rawName.startsWith('Dr.') ? rawName : `Dr. ${rawName}`;
  };

  const getMessage = (notification) => {
    const actorName = formatActorName(notification);

    if (notification.type === 'NEW_APPLICATION') {
      return <span><b>{actorName}</b> applied to your posting <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'NEW_COMMENT') {
      return <span><b>{actorName}</b> commented on <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'NEW_REPLY') {
      return <span><b>{actorName}</b> replied to your comment</span>;
    }
    if (notification.type === 'NEW_CLINICAL_INPUT') {
      return <span><b>{actorName}</b> added clinical input to <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'NEW_CLINICAL_REPLY') {
      return <span><b>{actorName}</b> replied to your clinical input on <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'NEW_LIKE') {
      return <span><b>{actorName}</b> liked your post <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'NEW_CASE_LIKE') {
      return <span><b>{actorName}</b> appreciated your case <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'NEW_CLINICAL_LIKE') {
      return <span><b>{actorName}</b> liked your clinical input on <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'ACCESS_REQUEST') {
      return <span><b>{actorName}</b> requested access to your case <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'ACCESS_APPROVED') {
      return <span><b>{actorName}</b> approved your access request for <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'ACCESS_REJECTED') {
      return <span><b>{actorName}</b> rejected your access request for <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'JOB_POSTED') {
      return <span><b>{actorName}</b> posted a new opportunity <b>{notification.entityTitle}</b></span>;
    }
    if (notification.type === 'APPLICATION_STATUS_UPDATED') {
      return <span>Your application for <b>{notification.entityTitle}</b> was updated by <b>{actorName}</b></span>;
    }
    // Fallback if message is provided directly from backend
    if (notification.message) return <span>{notification.message}</span>;
    
    return <span>New notification from <b>{actorName}</b></span>;
  };

  const getIcon = (type) => {
    if (type === 'NEW_APPLICATION') return <Briefcase size={12} className="text-[#6b46c1]" />;
    if (type === 'ACCESS_REQUEST' || type === 'ACCESS_APPROVED' || type === 'ACCESS_REJECTED') {
      return <Bell size={12} className="text-orange-500" />;
    }
    if (type === 'NEW_LIKE' || type === 'NEW_CLINICAL_LIKE') {
      return <span className="text-red-500 text-[10px]">❤️</span>;
    }
    if (type === 'NEW_CLINICAL_INPUT' || type === 'NEW_CLINICAL_REPLY') {
      return <MessageSquare size={12} className="text-teal-500" />;
    }
    return <MessageSquare size={12} className="text-[#6b46c1]" />;
  };

  return (
    <div className="relative" ref={menuRef}>
      <button 
        id="notification-bell" 
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
      >
        <Bell size={20} className="text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1.5 min-w-[16px] h-4 px-1 text-[10px] font-bold text-white bg-red-500 rounded-full flex items-center justify-center ring-2 ring-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="p-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
            <h3 className="font-bold text-gray-900">Notifications</h3>
            {unreadCount > 0 && (
              <button 
                onClick={() => markAllAsRead()}
                className="text-xs font-medium text-[#6b46c1] hover:text-[#553098] flex items-center gap-1"
              >
                <Check size={14} /> Mark all read
              </button>
            )}
          </div>
          
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">
                You're all caught up!
              </div>
            ) : (
              notifications.map((notification) => {
                const displayName = formatActorName(notification);
                const avatarUrl = notification.actor?.avatarUrl || notification.actorAvatar;
                const initials = (displayName || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

                return (
                  <div 
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`p-3.5 border-b border-gray-50 cursor-pointer hover:bg-gray-50 transition-colors flex gap-3 group relative ${!notification.isRead ? 'bg-[#6b46c1]/5' : ''}`}
                  >
                    {/* Actor Avatar with Overlay Type Icon */}
                    <div className="relative shrink-0 mt-0.5">
                      <div className="w-10 h-10 rounded-full bg-indigo-100 text-indigo-700 font-bold flex items-center justify-center text-xs overflow-hidden border border-indigo-200 shadow-xs">
                        {avatarUrl ? (
                          <img
                            src={avatarUrl}
                            alt={displayName}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          initials
                        )}
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full bg-white flex items-center justify-center shadow border border-gray-100">
                        {getIcon(notification.type)}
                      </div>
                    </div>

                    <div className="flex-1 pr-5 min-w-0">
                      <p className="text-xs text-gray-800 leading-snug">
                        {getMessage(notification)}
                      </p>
                      <span className="text-[10px] text-gray-400 mt-1 block font-medium">
                        {timeAgo(notification.createdAt)}
                      </span>
                    </div>
                    {!notification.isRead && (
                      <div className="w-2 h-2 bg-[#6b46c1] rounded-full mt-2 shrink-0" />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification.id);
                      }}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                      title="Delete notification"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;